import {
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/styles";
import React, { useEffect, useState } from "react";
import useExternalScriptLoader from "../hooks/useExternalScriptLoader";

const useStyles = makeStyles(theme => ({
  enquiryFormContainer: {
    minHeight: 1000
  },
  enquiryFormStepper: {
    padding: 0,
    marginBottom: theme.spacing(6)
  },
  enquiryFormErrorContainer: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(1)
  },
  enquiryFormSkeletonTitle: {
    backgroundColor: theme.palette.grey[200],
    width: 150,
    height: 25,
    marginBottom: theme.spacing(1.5)
  },
  enquiryFormSkeletonSubtitle: {
    backgroundColor: theme.palette.grey[200],
    width: "90%",
    height: 15
  },
  enquiryFormSkeletonGroup: {
    marginTop: theme.spacing(6)
  },
  enquiryFormSkeletonFieldTitle: {
    backgroundColor: theme.palette.grey[200],
    width: 200,
    height: 15,
    marginBottom: theme.spacing(1)
  },
  enquiryFormSkeletonCheckPoint: {
    backgroundColor: theme.palette.grey[200],
    display: "inline-block",
    width: "25%",
    height: 100,
    marginBottom: theme.spacing(1),
    borderRight: `${theme.spacing(1)}px solid white`
  },
  enquiryFormSkeletonInput: {
    backgroundColor: theme.palette.grey[200],
    width: "40%",
    height: 50
  }
}));

const EnquiryFormSkeleton = () => {
  const classes = useStyles();

  return (
    <React.Fragment>
      <div className={classes.enquiryFormSkeletonTitle} />
      <div className={classes.enquiryFormSkeletonSubtitle} />
      <div className={classes.enquiryFormSkeletonGroup}>
        <div className={classes.enquiryFormSkeletonFieldTitle} />
        {[0, 1, 2, 3, 4, 5].map(placeholder => (
          <div
            key={placeholder}
            className={classes.enquiryFormSkeletonCheckPoint}
          />
        ))}
      </div>
      {[0, 1, 2, 3, 4, 5].map(placeholder => (
        <div key={placeholder} className={classes.enquiryFormSkeletonGroup}>
          <div className={classes.enquiryFormSkeletonFieldTitle} />
          <div className={classes.enquiryFormSkeletonInput} />
        </div>
      ))}
    </React.Fragment>
  );
};

const EnquiryFormError = () => {
  const classes = useStyles();

  return (
    <div className={classes.enquiryFormErrorContainer}>
      <Typography variant="h4" component="div">
        Enquiry form failed to load
      </Typography>
      <Typography>
        There were errors loading the enquiry form, we're working to resolve
        them right now. Please reload to try again.
      </Typography>
    </div>
  );
};

const EnquiryForm = ({ type, steps = [], formOptions }) => {
  const classes = useStyles();
  const [loaded, error] = useExternalScriptLoader(
    "https://assets.cosmicapis.com/third_party/enquiry_form.js"
  );
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!loaded) {
      return;
    }

    const formInstance = window.cosmic.createForm(
      {
        el: "#external-enquiry-form",
        source: 2,
        type
      },
      formOptions
    );

    let step = activeStep;

    window.cosmic.on("NavigationAction", action => {
      if (action === "back") {
        step -= 1;
        setActiveStep(step);
      } else {
        step += 1;
        setActiveStep(step);
      }
    });

    return () => {
      window.cosmic.destroy();
      formInstance.$destroy();
    };
  }, [loaded]);

  if (error) {
    return <EnquiryFormError />;
  }

  if (!loaded) {
    return <EnquiryFormSkeleton />;
  }

  return (
    <React.Fragment>
      {steps.length > 0 && (
        <Stepper
          className={classes.enquiryFormStepper}
          activeStep={activeStep}
          alternativeLabel={mdDown}
        >
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
      <div
        className={classes.enquiryFormContainer}
        id="external-enquiry-form"
      />
    </React.Fragment>
  );
};

export default EnquiryForm;
